import React from 'react'
import * as Yup from 'yup'
import { Form, useFormikContext } from 'formik'
import carFormStyles from 'components/Car/CarForm.module.css'
import { useTranslation } from 'react-i18next'
import { SelectDriverField } from 'components/Car/SelectDriverField'
import { CarOptionalFeaturesForm } from 'components/Car/CarOptionalFeaturesForm'
import { FieldComponent } from 'components/Shared/FieldComponent'
import { TitleBar } from 'components/Shared/CrudComponents/TitleBar'
import { ContractorSelectField } from 'components/Carrier/CarrierSelectField'
import { FormColumn, FormRow } from 'components/Shared/CrudComponents'
import { FilesDropzone } from 'components/Shared/FilesDropzone'
import { uploadFile } from 'services/CarService'
import Sections from 'components/Car/sections'
import {
  useAuth,
  CAR_ATTACHMENTS_WRITE,
  CAR_CONTRACTOR_WRITE,
  CAR_DATA_WRITE,
  CAR_DRIVER_WRITE, CAR_CONTRACTOR_READ, CAR_DATA_READ, CAR_DRIVER_READ, CAR_ATTACHMENTS_READ
} from 'context/auth'

export const CarSchema = Yup.object().shape({
  brand: Yup.string()
    .required('form.validation.required'),
  model: Yup.string()
    .required('form.validation.required'),
  registrationNumber: Yup.string()
    .required('form.validation.required')
    .matches('^([A-Z0-9])+$', 'form.validation.carRegistrationNumber'),
  trailerRegistrationNumber: Yup.string(),
  type: Yup.string()
    .required('form.validation.required'),
  capacity: Yup.string()
    .required('form.validation.required'),
  loadingSpace: Yup.string()
    .required('form.validation.required'),
  driverId: Yup.string()
    .nullable(),
  carrierId: Yup.string()
    .required('form.validation.required')
    .nullable(),
  optionalFeatures: Yup.array(),
  filesIds: Yup.array()
})

export function mapCarToFormValues (car) {
  return ({
    brand: car.brand,
    model: car.model,
    registrationNumber: car.registrationNumber,
    trailerRegistrationNumber: car.trailerRegistrationNumber,
    type: car.type,
    capacity: car.capacity,
    loadingSpace: car.loadingSpace,
    optionalFeatures: car.optionalFeatures,
    driverId: car.driverId,
    carrierId: car.carrierId,
    filesIds: car?.files.map(file => file.id) || []
  })
}

export function CarForm ({ translateTitleLabel, initialFiles, isSubmitting }) {
  const { t } = useTranslation()
  const { hasAnyPrivileges } = useAuth()
  const { setFieldValue, values } = useFormikContext()

  return <Form>
    <TitleBar
      buttonLabel={t('form.car.submit')}
      title={t(`form.car.${translateTitleLabel}`)}
      isButtonDisabled={isSubmitting}/>
    <FormRow>
      <FormColumn section={Sections.Carrier}
                  disabled={!hasAnyPrivileges([CAR_CONTRACTOR_WRITE])}
                  visible={hasAnyPrivileges([CAR_CONTRACTOR_READ])}>
        <ContractorSelectField
          label={'form.car.carrier'}
          fieldName={'carrierId'}
          className={carFormStyles.select}
          role='CARRIER'
        />
      </FormColumn>
      <FormColumn section={Sections.Car}
                  disabled={!hasAnyPrivileges([CAR_DATA_WRITE])}
                  visible={hasAnyPrivileges([CAR_DATA_READ])}>
        <FieldComponent fieldName={'registrationNumber'} label={'form.car.registrationNumber'}/>
        <FieldComponent fieldName={'brand'} label={'form.car.brand'}/>
        <FieldComponent fieldName={'model'} label={'form.car.model'}/>
        <FieldComponent fieldName={'trailerRegistrationNumber'} label={'form.car.trailerRegistrationNumber'}/>
        <FieldComponent fieldName={'loadingSpace'} label={'form.car.loadingSpace'}/>
        <FieldComponent fieldName={'type'} label={'form.car.type'}/>
        <FieldComponent fieldName={'capacity'} label={'form.car.capacity'}/>
        <CarOptionalFeaturesForm values={values}/>
      </FormColumn>
      <FormColumn section={Sections.Driver}
                  disabled={!hasAnyPrivileges([CAR_DRIVER_WRITE])}
                  visible={hasAnyPrivileges([CAR_DRIVER_READ])}>
        <SelectDriverField
          fieldName="driverId"
        />
      </FormColumn>
      <FormColumn section={Sections.Files} className={carFormStyles.dropzoneSection}
                  disabled={!hasAnyPrivileges([CAR_ATTACHMENTS_WRITE])}
                  visible={hasAnyPrivileges([CAR_ATTACHMENTS_READ])}>
        <FilesDropzone initialFiles={initialFiles} onUpload={uploadFile}
                       onFilesUpdated={(updatedFiles) => setFieldValue('filesIds', updatedFiles.map(file => file.id))}/>
      </FormColumn>
    </FormRow>
  </Form>
}
